import { useEffect, useState, useMemo } from 'react';
import { api, downloadPdf, formatApiErrorDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Printer, RefreshCw, Search, History, X } from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { fi } from 'date-fns/locale';
import { toast } from 'sonner';

/**
 * Lähete-historia — lists printed delivery notes with one-click reprint.
 *
 * Filters: free-text search, date range. Defaults to last 14 days.
 */
export default function LaheteHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const today = format(new Date(), 'yyyy-MM-dd');
  const twoWeeksAgo = format(new Date(Date.now() - 14 * 86400000), 'yyyy-MM-dd');
  const [dateFrom, setDateFrom] = useState(twoWeeksAgo);
  const [dateTo, setDateTo] = useState(today);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      params.set('limit', '500');
      const { data } = await api.get(`/lahete/history?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Lähete-historian lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((it) => {
      const c = it.customer || {};
      return (
        (it.lahete_no || '').toLowerCase().includes(q) ||
        (c.asiakas || '').toLowerCase().includes(q) ||
        (c.ketju || '').toLowerCase().includes(q) ||
        (c.ryhma || '').toLowerCase().includes(q) ||
        (it.reitti || '').toLowerCase().includes(q) ||
        (it.ostotilaus || '').toLowerCase().includes(q) ||
        (it.printed_by || '').toLowerCase().includes(q)
      );
    });
  }, [items, query]);

  const reprint = async (it) => {
    if (!it.customer?.id || !it.date) {
      toast.error('Puuttuu asiakas tai päivämäärä — ei voi tulostaa uudelleen');
      return;
    }
    try {
      const fname = await downloadPdf(
        `/api/lahete/single/${it.date}/${it.customer.id}.pdf`,
        `Lähete_${(it.customer.asiakas || 'asiakas').replace(/ /g, '_')}_${it.date}.pdf`,
      );
      toast.success(`Ladattu: ${fname}`);
      // The reprint creates a new history row — reload
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Uudelleentulostus epäonnistui');
    }
  };

  return (
    <div className="mt-10 pt-6 border-t border-[#E5E6E3]" data-testid="lahete-history-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History className="text-[#2A4B30]" size={18} />
          <h2 className="font-heading text-lg font-semibold text-[#1C1C1A]">Lähete-historia</h2>
          <span className="text-xs text-[#6A6D66]">({filtered.length} {filtered.length === items.length ? 'kpl' : `/ ${items.length}`})</span>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="lahete-history-refresh"
          className="h-8 rounded-full border-[#E5E6E3]"
        >
          <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Päivitä
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6A6D66]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hae lähete-nro, asiakas, reitti, käyttäjä…"
            data-testid="lahete-history-search"
            className="h-8 pl-7 pr-7 w-72 text-xs"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-[#E5E6E3] flex items-center justify-center text-[#6A6D66]"
              aria-label="Tyhjennä haku"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <span className="text-[11px] text-[#6A6D66] ml-1">Pvm:</span>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          data-testid="lahete-history-date-from"
          className="h-8 w-36 text-xs"
        />
        <span className="text-[#6A6D66]">→</span>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          data-testid="lahete-history-date-to"
          className="h-8 w-36 text-xs"
        />
      </div>

      <div className="border border-[#E5E6E3] rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F4F6F3] text-[#6A6D66] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Lähete-nro</th>
                <th className="px-3 py-2 text-left">Toim.pvm</th>
                <th className="px-3 py-2 text-left">Asiakas</th>
                <th className="px-3 py-2 text-left">Reitti</th>
                <th className="px-3 py-2 text-right">Määrä</th>
                <th className="px-3 py-2 text-left">Ostotilaus</th>
                <th className="px-3 py-2 text-left">Tulostettu</th>
                <th className="px-3 py-2 text-left">Käyttäjä</th>
                <th className="px-3 py-2 text-center w-20">Uudelleen</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[#6A6D66]">Ei tuloksia annetuilla suodattimilla</td></tr>
              )}
              {filtered.map((it) => {
                const c = it.customer || {};
                let printedAgo = '';
                let printedFull = '';
                try {
                  const p = parseISO(it.printed_at);
                  printedAgo = formatDistanceToNow(p, { locale: fi, addSuffix: true });
                  printedFull = format(p, "d.M.yyyy HH:mm");
                } catch { /* noop */ }
                return (
                  <tr key={it.id} className="border-t border-[#F0F1ED] hover:bg-[#F9FAF8]" data-testid={`lahete-history-row-${it.id}`}>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{it.lahete_no || '—'}</td>
                    <td className="px-3 py-1.5 text-[#6A6D66]">{it.date}</td>
                    <td className="px-3 py-1.5">
                      <div className="font-medium">{c.ketju} {c.asiakas}</div>
                      <div className="text-[10px] text-[#6A6D66]">nro {c.nro} · {c.ryhma}</div>
                    </td>
                    <td className="px-3 py-1.5 font-medium">{it.reitti || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{it.maara_kg} kg</td>
                    <td className="px-3 py-1.5 text-[11px] text-[#6A6D66]">{it.ostotilaus || '—'}</td>
                    <td className="px-3 py-1.5 text-[11px] text-[#6A6D66]" title={printedFull}>{printedAgo}</td>
                    <td className="px-3 py-1.5 text-[11px] text-[#6A6D66]">{it.printed_by}</td>
                    <td className="px-3 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => reprint(it)}
                        data-testid={`lahete-history-reprint-${it.id}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#E8F0E9] hover:bg-[#D0E1D3] text-[#2A4B30] transition-colors"
                        title="Tulosta uudelleen"
                      >
                        <Printer size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
